import { RJSFValidationError } from '@rjsf/utils'
import toPath from 'lodash/toPath'

type WorkingStepValidationField = {
  key: string
  label: string
  messages: string[]
}

export type WorkingStepValidationGroup = {
  key: string
  label: string
  fields: WorkingStepValidationField[]
}

const humanize = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (character) => character.toUpperCase())

const errorPath = (error: RJSFValidationError): string[] => {
  const path = toPath(error.property ?? '')
  if (error.name === 'required') {
    const missingProperty = error.params?.missingProperty
    if (
      typeof missingProperty === 'string' &&
      path.at(-1) !== missingProperty
    ) {
      path.push(missingProperty)
    }
  }

  const last = path[path.length - 1]
  if (last === 'value' && path.length > 1) {
    path.pop()
  }
  return path.filter((segment) => segment.length > 0)
}

const groupLabel = (path: string[]): string => {
  const labels: string[] = []
  for (let index = 0; index < path.length; index += 1) {
    const segment = path[index]
    if (/^\d+$/.test(segment)) {
      labels.push(`Item ${Number(segment) + 1}`)
      continue
    }
    if (/^\d+$/.test(path[index + 1] ?? '')) continue
    labels.push(humanize(segment))
  }
  return labels.join(' – ') || 'General'
}

const normalizedMessage = (error: RJSFValidationError): string => {
  if (error.name === 'required' || /(?:^| )is required$/i.test(error.message)) {
    return 'Required'
  }
  return error.message || error.stack
}

export function getWorkingStepValidationGroups(
  errors: RJSFValidationError[],
): WorkingStepValidationGroup[] {
  const groups = new Map<string, WorkingStepValidationGroup>()
  for (const error of errors) {
    const path = errorPath(error)
    const key = path.join('.') || '__root__'
    const itemIndex = path.findLastIndex((segment) => /^\d+$/.test(segment))
    const itemPath = path.slice(0, itemIndex + 1)
    const itemKey = itemPath.join('.') || '__root__'
    const fieldPath = path.slice(itemIndex + 1)
    const message = normalizedMessage(error)
    let group = groups.get(itemKey)
    if (!group) {
      group = { key: itemKey, label: groupLabel(itemPath), fields: [] }
      groups.set(itemKey, group)
    }
    const field = group.fields.find((field) => field.key === key)
    if (field) {
      if (!field.messages.includes(message)) field.messages.push(message)
    } else {
      group.fields.push({
        key,
        label: groupLabel(fieldPath),
        messages: [message],
      })
    }
  }
  return [...groups.values()]
}
